import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useListProducts, getListProductsQueryKey, useUpdateProduct } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Tag, Package, Search, Edit2, Check, X, ChevronDown, ChevronRight } from "lucide-react";
import { Link } from "wouter";
import { formatCurrency } from "@/lib/currency";

export default function Brands() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: products, isLoading } = useListProducts({});
  const updateProduct = useUpdateProduct();

  const [search, setSearch] = useState("");
  const [renamingBrand, setRenamingBrand] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [expandedBrand, setExpandedBrand] = useState<string | null>(null);

  const brandMap = new Map<string, typeof products>(); 
  for (const p of (products || [])) {
    const brand = (p as any).brand as string | null;
    if (brand && brand.trim()) {
      if (!brandMap.has(brand)) brandMap.set(brand, []);
      brandMap.get(brand)!.push(p);
    }
  }

  const brands = Array.from(brandMap.entries())
    .map(([name, prods]) => ({ name, count: prods!.length, products: prods! }))
    .filter(b => !search || b.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const unbranded = (products || []).filter(p => !(p as any).brand);

  const startRename = (brand: string) => {
    setRenamingBrand(brand);
    setRenameValue(brand);
  };

  const handleRename = async () => {
    if (!renamingBrand || !renameValue.trim() || renameValue.trim() === renamingBrand) {
      setRenamingBrand(null);
      return;
    }
    const prods = brandMap.get(renamingBrand) || [];
    setRenaming(true);
    try {
      await Promise.all(prods.map(p =>
        updateProduct.mutateAsync({ id: p.id, data: { brand: renameValue.trim() } })
      ));
      queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
      setRenamingBrand(null);
    } catch {
      toast({ title: "Failed to rename brand", variant: "destructive" });
    } finally {
      setRenaming(false);
    }
  };

  const toggleBrand = (name: string) => {
    setExpandedBrand(prev => prev === name ? null : name);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Brands</h1>
          <p className="text-muted-foreground mt-1">
            {brandMap.size} brand{brandMap.size !== 1 ? "s" : ""} across {products?.length ?? 0} products
          </p>
        </div>
        <Link href="/inventory">
          <Button>
            <Package className="w-4 h-4 mr-2" /> Add Product with Brand
          </Button>
        </Link>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search brands..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        {unbranded.length > 0 && (
          <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 px-3 py-1.5">
            {unbranded.length} product{unbranded.length !== 1 ? "s" : ""} without a brand
          </Badge>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : brands.length === 0 ? (
        <div className="border rounded-md py-16 text-center space-y-3">
          <Tag className="w-10 h-10 mx-auto text-muted-foreground" />
          <p className="font-medium text-muted-foreground">
            {search ? "No brands match your search" : "No brands yet"}
          </p>
          {!search && (
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              Add a <strong>Brand</strong> when creating or editing a product in Inventory.
            </p>
          )}
          <Link href="/inventory">
            <Button variant="outline" className="mt-2">
              <Package className="w-4 h-4 mr-2" /> Go to Inventory
            </Button>
          </Link>
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader className="bg-background">
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Brand Name</TableHead>
                <TableHead>Products</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {brands.map(b => (
                <>
                  <TableRow
                    key={b.name}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleBrand(b.name)}
                  >
                    <TableCell className="pr-0">
                      {expandedBrand === b.name
                        ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Tag className="w-3.5 h-3.5 text-primary" />
                        </div>
                        {b.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{b.count} product{b.count !== 1 ? "s" : ""}</Badge>
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => startRename(b.name)}
                      >
                        <Edit2 className="w-3.5 h-3.5" /> Rename
                      </Button>
                    </TableCell>
                  </TableRow>

                  {expandedBrand === b.name && (
                    <TableRow key={`${b.name}-products`} className="bg-muted/30 hover:bg-muted/30">
                      <TableCell colSpan={4} className="p-0">
                        <div className="px-6 py-3 border-t border-dashed">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                            All {b.name} Products ({b.count})
                          </p>
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {b.products.map(p => (
                              <div key={p.id} className="flex items-center justify-between bg-background border rounded-lg px-3 py-2 gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate">{p.name}</p>
                                  <p className="text-xs text-muted-foreground">{(p as any).categoryName ?? "No category"}</p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <p className="text-sm font-semibold">{formatCurrency(Number(p.price))}</p>
                                  <p className={`text-xs ${Number(p.stock) <= Number(p.reorderLevel ?? 5) ? "text-amber-600 font-medium" : "text-muted-foreground"}`}>
                                    {p.stock} in stock
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!renamingBrand} onOpenChange={o => !o && setRenamingBrand(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Brand</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Renaming <strong>"{renamingBrand}"</strong> will update all {brandMap.get(renamingBrand || "")?.length ?? 0} product(s) that use this brand.
          </p>
          <Input
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            placeholder="New brand name"
            onKeyDown={e => e.key === "Enter" && handleRename()}
            autoFocus
          />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setRenamingBrand(null)}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={handleRename} disabled={renaming || !renameValue.trim()}>
              {renaming ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
